/* =============================================================================
 * Pacote de uma competicao.
 *
 * O que a navegacao basica faz pelo app, este faz pela competicao escolhida:
 * classificacao, rodadas, agenda e resultados continuam abrindo sem rede, com
 * todos os filtros de categoria - e nao so' o que a pessoa tinha aberto antes de
 * a internet cair.
 *
 * UMA CHAMADA, E NAO CINCO POR CATEGORIA. E' o ObterTabCompeticaoDadosOffline
 * (Negocio\Funcionalidade\Futigol) que monta tudo do lado do servidor. Uma
 * competicao com oito categorias sao 33 consultas; num 3G de estadio isso e' a
 * diferenca entre baixar e desistir.
 *
 * O BLOCO DA CATEGORIA ZERO nao e' uma categoria: e' o "todas as categorias" com
 * que a home e a agenda sem filtro chamam o servico. Ele so' tem jogos -
 * classificacao e rodadas exigem uma categoria de verdade.
 *
 * O QUE ELE NAO TRAZ: a sumula da partida (escalacao, lances, documentos). Sao
 * dezenas a centenas de consultas cheias por competicao. Mas a PARTIDA em si o
 * pacote tem - ela esta' dentro das rodadas e dos jogos -, e por isso a tela da
 * partida abre offline com o placar e a ficha, dizendo que a sumula nao foi
 * baixada. A partida aberta com rede continua abrindo inteira, pelo cache de
 * respostas - ver a lista de cacheaveis em offline.js.
 * ========================================================================== */

import { executarFuncionalidade } from '@shared/api/ServicoAplicacao.js';
import { competicaoAtual } from '@shared/sessao/competicao-atual.js';
import { urlImagem } from '@shared/lib/arquivos.js';
import { paraBooleano, paraData, paraInteiro, paraLista, paraTexto } from '@shared/lib/valores.js';
import * as offline from './offline.js';

export const TIPO = 'competicao';

const nomeDataset = (id) => `competicao-${paraInteiro(id)}`;
//A chave do bloco: `c0` e' "todas as categorias". O prefixo existe para a chave
//nunca virar indice numerico quando o objeto for serializado.
const chaveBloco = (idCategoria) => `c${paraInteiro(idCategoria)}`;

//Uma competicao e' consultada a cada troca de tela; o dataset dela fica em
//memoria depois da primeira leitura. Mais de uma pode estar baixada.
const emMemoria = new Map();

async function dataset(idCompeticao) {
    const id = paraInteiro(idCompeticao);
    if (emMemoria.has(id)) return emMemoria.get(id);
    const dados = await offline.obterDataset(nomeDataset(id));
    emMemoria.set(id, dados);
    return dados;
}

export function registrarPacoteCompeticao() {
    offline.registrarTipoPacote({
        tipo: TIPO,
        titulo: 'Competição',
        descricao: 'Classificação, rodadas, agenda e resultados de uma competição, com todas as categorias.',
        icone: 'trofeu',
        ordem: 2,
        escopo: 'porItem',
        dicaOrigem: 'Escolha a competição em /competicoes; é ela que fica disponível para baixar aqui.',
        alvoAtual,
        baixar,
        aoRemover: (pacote) => {
            emMemoria.delete(paraInteiro(pacote.id));
            //Memo de datas, sem dono por competicao: limpar inteiro e' mais barato
            //(e mais seguro) do que descobrir quais partidas eram deste pacote.
            dataDaPartida.clear();
        },
    });

    const disponivel = (func) => offline.pacoteDisponivel(TIPO, func.IdCompeticao);
    const dataPacote = (func) => offline.obterPacote(TIPO, func.IdCompeticao)?.dataDownload ?? null;

    offline.registrarResolvedor('ObterTabCompeticaoDetalhe', {
        podeResolver: disponivel,
        dataPacote,
        resolver: async (func) => (await dataset(func.IdCompeticao))?.detalhe ?? null,
    });

    offline.registrarResolvedor('ObterTabCompeticaoClassificacao', {
        podeResolver: disponivel,
        dataPacote,
        resolver: async (func) => {
            const d = await dataset(func.IdCompeticao);
            const bloco = d?.blocos?.[chaveBloco(func.IdCompeticaoCategoria)];
            //Classificacao null nao e' erro - e' competicao sem consulta
            //configurada (ver docs/API-CONTRATO.md). Mas o resolvedor precisa
            //devolver ALGO, senao o nucleo entende "nao sei responder" e a tela
            //fica esperando a rede que nao existe.
            return bloco ? { Competicao: d.detalhe?.Competicao ?? null, Classificacoes: bloco.Classificacoes ?? [] } : null;
        },
    });

    offline.registrarResolvedor('ObterTabCompeticaoRodadas', {
        //Fase > 0 nao esta' no pacote de proposito: a tela de rodadas pede
        //sempre fase 0 e recorta as fases no cliente (ver rodadas.js).
        podeResolver: (func) => disponivel(func) && paraInteiro(func.IdCompeticaoFase) === 0,
        dataPacote,
        resolver: async (func) => {
            const d = await dataset(func.IdCompeticao);
            const bloco = d?.blocos?.[chaveBloco(func.IdCompeticaoCategoria)];
            if (!bloco?.Rodadas) return null;
            return { Competicao: d.detalhe?.Competicao ?? null, CompeticaoCategoria: bloco.CompeticaoCategoria ?? null, Rodadas: bloco.Rodadas };
        },
    });

    offline.registrarResolvedor('ObterTabCompeticaoJogosMarcados', {
        podeResolver: disponivel,
        dataPacote,
        //O pacote guarda a agenda JA' COM as previstas, que e' como proximos
        //jogos pede (ver competicoes/jogos.js). Quem nao pediu recebe a lista sem
        //elas: sem esse recorte, jogos-marcados mostraria offline um jogo que com
        //rede nao aparece ali.
        resolver: (func) => jogos(func, 'JogosMarcados', { previstas: paraBooleano(func.IncluirPrevistas) }),
    });

    offline.registrarResolvedor('ObterTabCompeticaoJogosResultados', {
        podeResolver: disponivel,
        dataPacote,
        resolver: (func) => jogos(func, 'JogosResultados'),
    });

    /**
     * O calendario nao tem bloco proprio no pacote, e nao precisa: ele e' um
     * RECORTE POR DATA das partidas que ja' estao ali dentro. Recortar aqui vale
     * mais do que baixar um bloco por mes - o pacote nao cresce, e qualquer mes
     * abre, inclusive os que a pessoa nunca visitou online.
     */
    offline.registrarResolvedor('ObterTabCompeticaoCalendario', {
        podeResolver: disponivel,
        dataPacote,
        resolver: async (func) => {
            const d = await dataset(func.IdCompeticao);
            if (!d) return null;

            const inicio = numeroDoDia(func.DataInicial);
            const fim = numeroDoDia(func.DataFinal);
            if (!inicio || !fim) return null;

            const idCategoria = paraInteiro(func.IdCompeticaoCategoria);
            //DATA_PARTIDA e nao DATA_PREVISTA, e a hora fora da comparacao: e'
            //exatamente o criterio do C# (ver ObterTabCompeticaoCalendario.cs).
            const partidas = partidasDoDataset(d)
                .filter((p) => idCategoria === 0 || paraInteiro(p.IdCompeticaoCategoria) === idCategoria)
                .filter((p) => {
                    const dia = numeroDoDia(p.DataPartida);
                    return dia >= inicio && dia <= fim;
                })
                .sort(compararPartidas);

            return { Competicao: d.detalhe?.Competicao ?? null, Partidas: partidas };
        },
    });

    /**
     * A partida SEM SUMULA, montada do pacote da competicao.
     *
     * FALLBACK, e nao resolvedor normal: quem abriu a partida com rede tem a
     * sumula inteira no cache de respostas, e ela vale mais do que esta ficha
     * reduzida - por isso o nucleo tenta o cache primeiro (ver offline.js).
     *
     * `SumulaIndisponivelOffline` viaja na resposta porque a tela PRECISA saber a
     * diferenca: escalacao vazia aqui nao significa "nao divulgada", significa
     * "nao baixada", e as duas frases mandam a pessoa fazer coisas diferentes.
     *
     * O id da partida nao diz de que competicao ela e', entao `podeResolver` so'
     * pode afirmar "ha' competicao baixada"; quem procura de fato e' o resolver.
     */
    offline.registrarResolvedor('ObterTabPartidaDetalhe', {
        podeResolver: () => offline.pacotesDoTipo(TIPO).some((p) => p.situacao === 'concluido'),
        fallback: true,
        //Preenchido pelo resolver logo antes - o nucleo chama os dois em sequencia.
        dataPacote: (func) => dataDaPartida.get(paraInteiro(func.IdPartida)) ?? null,
        resolver: async (func) => {
            const achado = await localizarPartida(func.IdPartida);
            if (!achado) return null;

            dataDaPartida.set(paraInteiro(func.IdPartida), achado.pacote.dataDownload ?? null);
            return {
                Partida: achado.partida,
                Competicao: achado.dados.detalhe?.Competicao ?? null,
                TimeMandante: null,
                TimeVisitante: null,
                AtletasMandantes: [],
                AtletasVisitantes: [],
                Eventos: [],
                Anexos: [],
                SumulaIndisponivelOffline: true,
            };
        },
    });
}

async function jogos(func, campo, { previstas = true } = {}) {
    const d = await dataset(func.IdCompeticao);
    const bloco = d?.blocos?.[chaveBloco(func.IdCompeticaoCategoria)];
    if (!bloco?.[campo]) return null;
    return { Competicao: d.detalhe?.Competicao ?? null, GruposPartidas: previstas ? bloco[campo] : semPrevistas(bloco[campo]) };
}

/**
 * A agenda sem o jogo de data so' prevista, no formato do backend (PascalCase).
 * O dia que fica sem partida sai da lista - o servico nao devolve grupo vazio, e
 * a tela desenharia um titulo de dia com nada embaixo.
 */
function semPrevistas(grupos) {
    return paraLista(grupos)
        .filter(Boolean)
        .map((g) => ({ ...g, Partidas: paraLista(g?.Partidas).filter((p) => paraTexto(p?.TipoSituacaoPartida).toUpperCase() !== 'PREVISTA') }))
        .filter((g) => g.Partidas.length > 0);
}

/* ---- partidas dentro do pacote ------------------------------------------ */

const dataDaPartida = new Map();

/**
 * Toda partida que o pacote guarda, sem repetir.
 *
 * A MESMA PARTIDA APARECE TRES VEZES: na rodada dela, na agenda da categoria e na
 * agenda geral (o bloco `c0`). Sem o Map por IdPartida o calendario mostraria o
 * mesmo jogo tres vezes na mesma celula.
 *
 * As rodadas entram junto com os dois recortes de jogos porque nenhum dos tres
 * cobre tudo sozinho: agenda e' MARCADA/ANDAMENTO/PREVISTA, resultados e'
 * REALIZADA, e o que a liga cadastrar fora disso (adiada, cancelada) so' aparece
 * pela rodada.
 */
function partidasDoDataset(d) {
    const mapa = new Map();

    for (const bloco of Object.values(d?.blocos ?? {})) {
        const grupos = [...paraLista(bloco?.JogosMarcados), ...paraLista(bloco?.JogosResultados), ...paraLista(bloco?.Rodadas).flatMap((rodada) => paraLista(rodada?.GruposPartidas))];
        for (const grupo of grupos) {
            for (const partida of paraLista(grupo?.Partidas)) {
                const id = paraInteiro(partida?.IdPartida);
                if (id > 0 && !mapa.has(id)) mapa.set(id, partida);
            }
        }
    }

    return [...mapa.values()];
}

/** A partida entre TODAS as competicoes baixadas - o id nao diz de qual ela e'. */
async function localizarPartida(idPartida) {
    const id = paraInteiro(idPartida);
    if (id <= 0) return null;

    for (const pacote of offline.pacotesDoTipo(TIPO)) {
        if (pacote.situacao !== 'concluido') continue;
        const dados = await dataset(pacote.id);
        const partida = partidasDoDataset(dados).find((p) => paraInteiro(p.IdPartida) === id);
        if (partida) return { partida, dados, pacote };
    }

    return null;
}

/** aaaammdd: comparavel, e sem a hora atrapalhar. Zero quando nao ha' data. */
function numeroDoDia(valor) {
    const data = paraData(valor);
    if (!data) return 0;
    return data.getFullYear() * 10000 + (data.getMonth() + 1) * 100 + data.getDate();
}

function minutosDoDia(valor) {
    const hora = paraData(valor);
    return hora ? hora.getHours() * 60 + hora.getMinutes() : 0;
}

/**
 * Mesma ordem do C#: DATA_PARTIDA, HORA_PARTIDA, ID_PARTIDA. Comparacao em
 * cascata, e nao uma chave numerica unica: dia+hora+id somados passariam de
 * Number.MAX_SAFE_INTEGER e duas partidas do mesmo dia empatariam por
 * arredondamento.
 */
function compararPartidas(a, b) {
    return numeroDoDia(a?.DataPartida) - numeroDoDia(b?.DataPartida) || minutosDoDia(a?.HoraPartida) - minutosDoDia(b?.HoraPartida) || paraInteiro(a?.IdPartida) - paraInteiro(b?.IdPartida);
}

/**
 * A competicao que a page oferece baixar: a que esta' em uso.
 *
 * Ela e' o contexto em que o app inteiro esta' (ver sessao/competicao-atual.js),
 * entao e' a unica escolha que nao obriga a pessoa a repetir aqui uma decisao que
 * ela ja' tomou. Trocar de competicao e voltar oferece a outra.
 */
function alvoAtual() {
    const competicao = competicaoAtual();
    if (!competicao) return null;
    return { id: competicao.id, titulo: competicao.descricao || `Competição #${competicao.id}`, subtitulo: competicao.liga };
}

/* ---- download ----------------------------------------------------------- */

async function baixar(idCompeticao, { aoProgredir = () => {}, controle = {} } = {}) {
    const id = paraInteiro(idCompeticao);
    if (id <= 0) throw new Error('Escolha uma competição antes de baixá-la.');

    aoProgredir({ etapa: 'dados', descricao: 'Obtendo a competição…', percentual: 5 });

    const r = await executarFuncionalidade({
        NomeFuncionalidade: 'ObterTabCompeticaoDadosOffline',
        IdCompeticao: id,
        BuscarClassificacoes: true,
        BuscarRodadas: true,
        BuscarJogos: true,
    });

    if (!r?.Competicao) throw new Error('O servidor não devolveu a competição. Confira se a versão da API tem a funcionalidade ObterTabCompeticaoDadosOffline.');

    aoProgredir({ etapa: 'montando', descricao: 'Organizando categorias e jogos…', percentual: 25 });
    const novo = montarDataset(r);

    const imagens = urlsDeImagens(r, novo);
    const { baixadas, bytes } = await offline.baixarImagens(imagens, {
        controle,
        aoProgredir,
        inicial: 35,
        final: 95,
        descricao: 'Baixando escudos…',
    });

    aoProgredir({ etapa: 'gravando', descricao: 'Gravando no aparelho…', percentual: 97 });
    emMemoria.set(id, novo);

    if (!(await offline.gravarDataset(nomeDataset(id), novo))) {
        throw new Error('Não foi possível gravar o conteúdo no aparelho. Verifique o espaço disponível.');
    }

    const resumo = r.Resumo ?? {};
    const pacote = await offline.salvarPacote({
        tipo: TIPO,
        id,
        titulo: paraTexto(r.Competicao.Descricao) || `Competição #${id}`,
        subtitulo: paraTexto(r.Competicao.Liga) || 'Competição',
        icone: 'trofeu',
        //Com o id na URL, e nao o caminho curto: abrir daqui pode ser uma
        //competicao que NAO e' a selecionada, e e' a guarda `exigirCompeticao`
        //que troca a selecao quando o id vem no caminho (ver app/rotas.js).
        rota: `/competicoes/home/${id}`,
        nroItens: paraInteiro(resumo.NroPartidas),
        nroImagens: baixadas,
        bytes,
        situacao: controle.cancelado ? 'parcial' : 'concluido',
        datasets: [nomeDataset(id)],
        imagens,
        resumo: {
            categorias: paraInteiro(resumo.NroCategorias),
            rodadas: paraInteiro(resumo.NroRodadas),
            partidas: paraInteiro(resumo.NroPartidas),
            //Categoria ignorada e' o unico numero do resumo que a page mostra em
            //tom de aviso: o pacote existe, mas nao esta' inteiro.
            categoriasIgnoradas: paraInteiro(resumo.CategoriasIgnoradas),
        },
        dataDownload: new Date(),
    });

    aoProgredir({ etapa: 'concluido', descricao: 'Competição disponível offline.', percentual: 100 });
    return pacote;
}

/**
 * Converte a resposta nas duas formas que os resolvedores esperam: o bloco
 * `detalhe`, identico ao que ObterTabCompeticaoDetalhe devolveria, e um mapa de
 * blocos por categoria.
 */
function montarDataset(r) {
    const detalhe = {
        Competicao: r.Competicao,
        Categorias: paraLista(r.Categorias),
        Fases: paraLista(r.Fases),
        //As duas que a funcionalidade de origem declara e nunca preenche - ver o
        //cabecalho de obterJogosDaCompeticao em pages/home.js.
        PartidaAoVivo: [],
        ProximasPartidas: [],
    };

    const blocos = {};
    const categorias = paraLista(r.Categorias);
    for (const dados of [r.DadosCompeticao, ...paraLista(r.DadosCategorias)].filter(Boolean)) {
        const idCategoria = paraInteiro(dados.IdCompeticaoCategoria);
        blocos[chaveBloco(idCategoria)] = {
            //A tela de rodadas mostra o nome da categoria vindo da resposta; ele
            //ja' esta' em Categorias, entao nao vale uma consulta a mais.
            CompeticaoCategoria: categorias.find((c) => paraInteiro(c.IdCompeticaoCategoria) === idCategoria) ?? null,
            Classificacoes: paraLista(dados.Classificacoes),
            Rodadas: paraLista(dados.Rodadas),
            JogosMarcados: paraLista(dados.JogosMarcados),
            JogosResultados: paraLista(dados.JogosResultados),
        };
    }

    return { detalhe, blocos, resumo: r.Resumo ?? {} };
}

/** Os escudos de tudo que o pacote mostra: a competicao, a tabela e os jogos. */
function urlsDeImagens(r, novo) {
    const caminhos = [paraTexto(r.Competicao?.Imagem)];

    for (const bloco of Object.values(novo.blocos)) {
        for (const linha of bloco.Classificacoes) caminhos.push(paraTexto(linha?.ClubeLogo));

        const grupos = [...bloco.JogosMarcados, ...bloco.JogosResultados, ...bloco.Rodadas.flatMap((rodada) => paraLista(rodada?.GruposPartidas))];
        for (const grupo of grupos) {
            for (const partida of paraLista(grupo?.Partidas)) {
                caminhos.push(paraTexto(partida?.ClubeMandanteLogo));
                caminhos.push(paraTexto(partida?.ClubeVisitanteLogo));
            }
        }
    }

    //O mesmo escudo aparece em dezenas de partidas: sem o Set o download baixaria
    //a mesma imagem centenas de vezes (e o progresso mentiria).
    return [...new Set(caminhos.filter(Boolean).map((caminho) => urlImagem(caminho)))].filter(Boolean);
}
